/**
 * Контент-план — таргеты по типам публикаций (Б1.8 / Б5.5).
 *
 * Хранилище решено: таблица `content_plan_target` (`0001_init.sql:989`), писатель —
 * `POST /api/v1/content-plan` слушателя ui, человек (05-seams §8.2). План — это ЦЕЛИ
 * («за период: 2 news, 1 opinion»), а не расписание слотов и не список тем: слоты задаёт
 * конфиг бренда, темы рождает мониторинг (02-knowledge §11.1). Прогресс по целям в плане
 * НЕ хранится — он считается на чтении, иначе слой B промпта переписывался бы после
 * каждой публикации.
 *
 * ЕДИНСТВЕННОЕ ИСКЛЮЧЕНИЕ ПО ВРЕМЕНИ ВО ВСЕЙ СХЕМЕ. `period_start_local` — локальная дата
 * 'YYYY-MM-DD' в зоне бренда, всегда понедельник, а не миллисекунды UTC: «неделя»
 * определена в зоне бренда (05-seams §7.5), и один и тот же понедельник в разных
 * смещениях — это разные инстанты, то есть ключ строки перестал бы быть ключом. По той же
 * причине `from`/`to` маршрута чтения — локальные даты. Все остальные времена ответа
 * (`updated_at`) остаются ISO UTC, как везде.
 *
 * Календарная арифметика (понедельник ли дата, где кончается период) живёт здесь, а не в
 * `time.ts`: она не знает ни одного инстанта и ни одной зоны — это счёт суток по
 * григорианскому календарю, и дом времени ей не нужен. Правило R11 запрещает `Date` вне
 * `time.ts`; здесь `Date` и не появляется — только целые.
 *
 * Словарь форматов бэкендом НЕ проверяется — решение 02-knowledge §8.4 («бэкенд не читает
 * базу знаний»). Проверяется только ФОРМА кода: тот же вид, что у id файлов
 * `knowledge/formats/*.md`, потому что именно он уходит в `draft_version.format_code`
 * дословно (ФОР-2). Опечатка `newz` в план попадёт, и увидит её человек на этом же экране:
 * у выдуманного формата навсегда «занято 0».
 */

import * as z from 'zod';
import { ISO_UTC_MS } from '../time.ts';

/** Локальная дата 'YYYY-MM-DD' — форма ключа периода и границ маршрута чтения. */
export const LOCAL_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Форма кода формата: латиница, цифры и дефис между сегментами — `news`, `mini-research`.
 * Это НЕ словарь: список форматов живёт файлами в `knowledge/formats/`, и связи с БД у
 * него нет (04-integration-gaps B13).
 */
export const FORMAT_CODE_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** Горизонт плана — 2–4 недели (Б1.8); одна неделя допустима как частный случай. */
export const CONTENT_PLAN_MAX_WEEKS = 4;

/**
 * Потолок цели по одному формату. Не бизнес-ограничение, а защита от опечатки на месте
 * степпера: 300 постов за неделю при двух слотах — это не план, это промах по клавише.
 */
export const CONTENT_PLAN_MAX_TARGET = 99;

/** Потолок числа форматов в одном периоде: библиотека форматов — пять файлов, не сто. */
export const CONTENT_PLAN_MAX_FORMATS = 16;

/** Потолок окна чтения: экран показывает четыре недели, годовой запрос — ошибка вызова. */
export const CONTENT_PLAN_MAX_RANGE_DAYS = 366;

const LocalDate = z.string().regex(LOCAL_DATE_RE);
const IsoUtcMs = z.string().regex(ISO_UTC_MS);
const FormatCode = z.string().regex(FORMAT_CODE_RE).max(64);

// ─────────────────────────────────────────────────────────────────────────────
// Календарь: сутки — целое число, зон здесь нет

const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31] as const;

function isRealCalendarDay(year: number, month: number, day: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false;
  const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
  const last = month === 2 && leap ? 29 : (DAYS_IN_MONTH[month - 1] as number);
  return day <= last;
}

/**
 * 'YYYY-MM-DD' → номер суток от 1970-01-01 (алгоритм Хиннанта). `null` — строка не дата
 * или дата несуществующая: `2026-02-30` обязана отвергаться, а не молча становиться
 * первым марта, иначе понедельник проверялся бы у выдуманного дня.
 */
export function daysFromLocalDate(date: string): number | null {
  const m = LOCAL_DATE_RE.exec(date);
  if (m === null) return null;
  const [, ys, ms, ds] = m as unknown as [string, string, string, string];
  const year = Number(ys);
  const month = Number(ms);
  const day = Number(ds);
  if (!isRealCalendarDay(year, month, day)) return null;
  const y = year - (month <= 2 ? 1 : 0);
  const era = Math.floor(y / 400);
  const yoe = y - era * 400;
  const doy = Math.floor((153 * (month + (month > 2 ? -3 : 9)) + 2) / 5) + day - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

const PAD2 = (n: number): string => (n < 10 ? `0${n}` : String(n));

/** Обратное преобразование: номер суток → 'YYYY-MM-DD'. */
export function localDateFromDays(days: number): string {
  const z0 = days + 719468;
  const era = Math.floor(z0 / 146097);
  const doe = z0 - era * 146097;
  const yoe = Math.floor(
    (doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365,
  );
  const y = yoe + era * 400;
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const day = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const month = mp + (mp < 10 ? 3 : -9);
  const year = y + (month <= 2 ? 1 : 0);
  return `${String(year).padStart(4, '0')}-${PAD2(month)}-${PAD2(day)}`;
}

/** Сдвиг локальной даты на целые сутки. `null` — на входе не дата. */
export function localDatePlusDays(date: string, days: number): string | null {
  const d = daysFromLocalDate(date);
  return d === null ? null : localDateFromDays(d + days);
}

/**
 * Понедельник ли. 1970-01-01 — четверг, отсюда сдвиг 3: остаток по модулю 7 плюс 3 даёт
 * индекс от понедельника, и ноль означает понедельник.
 */
export function isLocalMonday(date: string): boolean {
  const d = daysFromLocalDate(date);
  return d !== null && ((((d % 7) + 7) % 7) + 3) % 7 === 0;
}

/** Понедельник недели, в которую попала дата. `null` — на входе не дата. */
export function mondayOfLocalDate(date: string): string | null {
  const d = daysFromLocalDate(date);
  if (d === null) return null;
  return localDateFromDays(d - (((((d % 7) + 7) % 7) + 3) % 7));
}

// ─────────────────────────────────────────────────────────────────────────────
// GET /api/v1/content-plan?brand&from&to

/**
 * Цель по одному формату внутри периода.
 *
 * `target_count = null` — цели в плане НЕТ, и это не то же самое, что ноль: ноль означает
 * «этот формат в периоде не публикуем», отсутствие — «про этот формат не решали». Строка с
 * `null` появляется в ответе, когда формат виден по занятым слотам, но в плане не заведён:
 * иначе формат, съедающий слоты мимо плана, был бы не виден вовсе.
 *
 * `committed_count` — сколько слотов периода уже держат посты этого формата (запланированные
 * и опубликованные). Это прогресс, слой C (02-knowledge §11.1): он считается на чтении и в
 * плане не хранится.
 */
export const ContentPlanTargetSchema = z.strictObject({
  format_code: FormatCode,
  target_count: z.int().min(0).max(CONTENT_PLAN_MAX_TARGET).nullable(),
  committed_count: z.int().nonnegative(),
  /** Логин человека, правившего цель последним; null — строки плана нет. */
  updated_by: z.string().nullable(),
  updated_at: IsoUtcMs.nullable(),
});
export type ContentPlanTarget = z.output<typeof ContentPlanTargetSchema>;

/**
 * Период плана. Сумма целей и число слотов расходятся законно — план не квота, — но
 * расхождение обязано быть НАЗВАНО в ответе, а не обнаружено человеком в конце недели:
 * `slots_delta = targets_total − slots_total`, больше нуля — целей больше, чем слотов
 * (часть форматов не выйдет), меньше нуля — слоты останутся пустыми.
 *
 * `covered_by_period_start` непусто, когда этот понедельник уже накрыт более ранним
 * многонедельным периодом: карточка показывается, но заводить в ней вторые цели — значит
 * планировать одну неделю дважды.
 */
export const ContentPlanPeriodSchema = z.strictObject({
  period_start_local: LocalDate,
  /** Последние сутки периода включительно — подпись, а не граница запроса. */
  period_end_local: LocalDate,
  period_weeks: z.int().min(1).max(CONTENT_PLAN_MAX_WEEKS),
  targets: z.array(ContentPlanTargetSchema),
  targets_total: z.int().nonnegative(),
  slots_total: z.int().nonnegative(),
  slots_free: z.int().nonnegative(),
  slots_delta: z.int(),
  /** Занятые слоты периода, у постов которых формат не проставлен вовсе. */
  committed_unformatted: z.int().nonnegative(),
  covered_by_period_start: LocalDate.nullable(),
});
export type ContentPlanPeriod = z.output<typeof ContentPlanPeriodSchema>;

/**
 * Ответ чтения. `known_formats` — словарь, который экран предлагает человеку: коды,
 * встречавшиеся у этого бренда в планах и на занятых слотах. Библиотеку форматов бэкенд не
 * читает (§8.4), поэтому предложить он может только то, что уже было.
 */
export const ContentPlanResponseSchema = z.strictObject({
  brand: z.string().min(1),
  /** Зона бренда: без неё «понедельник» на экране — не тот же понедельник, что в базе. */
  timezone: z.string().min(1),
  from_local: LocalDate,
  to_local: LocalDate,
  periods: z.array(ContentPlanPeriodSchema),
  known_formats: z.array(FormatCode),
});
export type ContentPlanResponse = z.output<typeof ContentPlanResponseSchema>;

// ─────────────────────────────────────────────────────────────────────────────
// POST /api/v1/content-plan

export const ContentPlanTargetInputSchema = z.strictObject({
  format_code: FormatCode,
  target_count: z.int().min(0).max(CONTENT_PLAN_MAX_TARGET),
});
export type ContentPlanTargetInput = z.output<typeof ContentPlanTargetInputSchema>;

/**
 * Тело правки: цели ОДНОГО периода целиком. Не патч по одной строке — иначе «убрать формат
 * из плана» пришлось бы выражать отдельным глаголом, а два глагола на одну таблицу
 * разъезжаются. Пустой `targets` — законное «целей на период нет», он стирает период.
 *
 * Понедельник проверяет схема, а не только домен: правило календарное, а не доменное, и
 * жить оно обязано там же, где объявлена форма даты.
 */
export const ContentPlanUpsertBodySchema = z
  .strictObject({
    brand: z.string().min(1).max(32),
    period_start_local: LocalDate,
    period_weeks: z.int().min(1).max(CONTENT_PLAN_MAX_WEEKS),
    targets: z.array(ContentPlanTargetInputSchema).max(CONTENT_PLAN_MAX_FORMATS),
  })
  .check((ctx) => {
    const v = ctx.value;
    if (!isLocalMonday(v.period_start_local)) {
      ctx.issues.push({
        code: 'custom',
        input: v.period_start_local,
        path: ['period_start_local'],
        message: 'period_start_must_be_monday',
      });
    }
    const seen = new Set<string>();
    for (const [i, t] of v.targets.entries()) {
      if (seen.has(t.format_code)) {
        ctx.issues.push({
          code: 'custom',
          input: t.format_code,
          path: ['targets', i, 'format_code'],
          message: 'duplicate_format_code',
        });
      }
      seen.add(t.format_code);
    }
  });
export type ContentPlanUpsertBody = z.output<typeof ContentPlanUpsertBodySchema>;
